import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useApp } from "@/context/AppContext";
import { DEPARTMENTS, COLLECTIONS } from "@/data/site";

export function AnnouncementBar() {
  return (
    <div className="bg-gold text-black text-center text-[0.7rem] font-mono uppercase tracking-[0.25em] py-2 px-4" data-testid="announcement-bar">
      Wear Confidence · No One Is Left Behind · HYEAMAN, also known as Street Sheriff
    </div>
  );
}

export function Header() {
  const { cartCount, wishlist, setCartOpen, setWishOpen, setSearchOpen, user } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { label: "Shop", to: "/shop" },
    ...DEPARTMENTS.slice(0, 3).map((d) => ({ label: d.label, to: `/shop?department=${d.key}` })),
    { label: "Black Label", to: "/shop?collection=black-label" },
    { label: "Street Sheriff", to: "/street-sheriff" },
    { label: "Lookbook", to: "/lookbook" },
    { label: "About", to: "/about" },
  ];

  return (
    <>
      <AnnouncementBar />
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 border-b transition-all duration-300 ${
          scrolled ? "border-white/10 bg-black/90 backdrop-blur-xl shadow-2xl" : "border-transparent bg-black"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-12">
          <div className="flex items-center gap-6">
            <button className="lg:hidden text-cream" onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="mobile-menu-open">
              <Menu size={22} />
            </button>
            <Logo />
          </div>

          <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
            {nav.map((n) => (
              <Link key={n.label} to={n.to}
                className="relative text-xs font-medium uppercase tracking-[0.2em] text-cream/80 transition-colors hover:text-gold after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all hover:after:w-full">
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4 sm:gap-5">
            <button onClick={() => setSearchOpen(true)} aria-label="Search" data-testid="open-search" className="text-cream/80 hover:text-gold transition-colors">
              <Search size={19} />
            </button>
            <button onClick={() => (user ? navigate("/profile") : navigate("/login"))} aria-label="Account" data-testid="account-link" className="text-cream/80 hover:text-gold transition-colors">
              <User size={19} />
            </button>
            <button onClick={() => setWishOpen(true)} aria-label="Wishlist" data-testid="open-wishlist" className="relative text-cream/80 hover:text-gold transition-colors">
              <Heart size={19} />
              {wishlist.length > 0 && <Badge n={wishlist.length} />}
            </button>
            <button onClick={() => setCartOpen(true)} aria-label="Shopping bag" data-testid="open-cart" className="relative text-cream/80 hover:text-gold transition-colors">
              <ShoppingBag size={19} />
              {cartCount > 0 && <Badge n={cartCount} />}
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-nav">
          <div className="absolute inset-0 bg-black/80" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-4/5 max-w-xs bg-ink border-r border-gold/20 p-6 reveal">
            <div className="flex items-center justify-between mb-8">
              <Logo compact />
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="text-cream"><X size={22} /></button>
            </div>
            <div className="flex flex-col gap-1">
              <Link to="/shop" onClick={() => setMobileOpen(false)} className="py-3 border-b border-white/10 uppercase tracking-widest text-sm text-cream hover:text-gold">Shop All</Link>
              {DEPARTMENTS.map((d) => (
                <Link key={d.key} to={`/shop?department=${d.key}`} onClick={() => setMobileOpen(false)} className="py-3 border-b border-white/10 uppercase tracking-widest text-sm text-cream hover:text-gold">{d.label}</Link>
              ))}
              {COLLECTIONS.map((c) => (
                <Link key={c.key} to={`/shop?collection=${c.key}`} onClick={() => setMobileOpen(false)} className="py-3 border-b border-white/10 uppercase tracking-widest text-xs text-gold/80 hover:text-gold">{c.label}</Link>
              ))}
              <Link to="/street-sheriff" onClick={() => setMobileOpen(false)} className="py-3 border-b border-white/10 uppercase tracking-widest text-sm text-cream hover:text-gold">Street Sheriff</Link>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="py-3 uppercase tracking-widest text-sm text-cream hover:text-gold">About</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Badge({ n }) {
  return (
    <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center bg-gold px-1 text-[0.6rem] font-bold text-black">
      {n}
    </span>
  );
}
